<template>
  <div class="groupmap-container">
    <HeaderBar />
    <div class="groupmap-main">
      <div
        v-for="(row, rowIndex) in 20"
        :key="'group-row-' + rowIndex"
        class="group-row"
        :style="{ background: groupColors[rowIndex % groupColors.length] }"
      >
        <div
          v-for="(col, colIndex) in 10"
          :key="'group-cell-' + rowIndex + '-' + colIndex"
          class="group-cell"
          draggable="true"
          @dragstart="handleDragStart(rowIndex * 10 + colIndex)"
          @dragover.prevent
          @drop="handleDrop(rowIndex * 10 + colIndex)"
        >
          <span v-if="students[rowIndex * 10 + colIndex]">
            {{ students[rowIndex * 10 + colIndex].username }}
            <span v-if="colIndex === 0 && students[rowIndex * 10 + colIndex]">👑</span>
          </span>
        </div>
      </div>
    </div>
    <el-button type="primary" @click="saveGroup" :loading="saving" class="save-btn">保存分组</el-button>
  </div>
</template>

<script>
import HeaderBar from '@/components/HeaderBar.vue';
import { getUserList } from '@/api/index.js';
import { ElMessage } from 'element-plus';
import axios from 'axios';

export default {
  name: 'GroupMap',
  components: { HeaderBar },
  data() {
    return {
      students: [], // 200个格子
      dragIndex: null,
      saving: false,
      groupColors: [
        '#e53935', // 红
        '#8e24aa', // 紫
        '#3949ab', // 蓝
        '#039be5', // 天蓝
        '#00897b', // 青
        '#43a047', // 绿
        '#fbc02d', // 黄
        '#fb8c00', // 橙
        '#6d4c41', // 棕
        '#d81b60', // 粉
        '#1e88e5', // 深蓝
        '#00acc1', // 湖蓝
        '#7cb342', // 浅绿
        '#f4511e', // 深橙
        '#757575', // 灰
        '#c62828', // 深红
        '#ad1457', // 深粉
        '#283593', // 靛蓝
        '#00838f', // 深青
        '#2e7d32'  // 深绿
      ],
    };
  },
  async mounted() {
    await this.loadStudents();
  },
  methods: {
    async loadStudents() {
      const res = await getUserList();
      if (res.code === '000000') {
        // 先按groupId和groupLeader排好
        const seats = Array(200).fill(null);
        const noGroup = [];
        res.data.forEach(s => {
          if (s.groupId && s.groupId > 0 && s.groupId <= 20 && s.groupLeader !== undefined) {
            // 组长排在每组第一个
            const base = (s.groupId - 1) * 10;
            if (s.groupLeader === 1) {
              seats[base] = s;
            } else {
              // 找到组内第一个空位（除组长）
              for (let i = 1; i < 10; i++) {
                if (!seats[base + i]) {
                  seats[base + i] = s;
                  break;
                }
              }
            }
          } else {
            noGroup.push(s);
          }
        });
        // 没分组的顺序补到空位
        let idx = 0;
        for (let i = 0; i < seats.length; i++) {
          if (!seats[i] && idx < noGroup.length) {
            // 默认分到第一个空组
            seats[i] = { ...noGroup[idx], groupId: Math.floor(i / 10) + 1, groupLeader: i % 10 === 0 ? 1 : 0 };
            idx++;
          }
        }
        this.students = seats;
      }
    },
    handleDragStart(idx) {
      this.dragIndex = idx;
    },
    handleDrop(idx) {
      if (this.dragIndex === null || this.dragIndex === idx) return;
      // 交换分组信息
      const temp = this.students[idx];
      this.students[idx] = this.students[this.dragIndex];
      this.students[this.dragIndex] = temp;
      // 更新groupId和groupLeader
      this.updateGroupInfo(idx);
      this.updateGroupInfo(this.dragIndex);
      this.dragIndex = null;
    },
    updateGroupInfo(idx) {
      const row = Math.floor(idx / 10);
      const col = idx % 10;
      if (this.students[idx]) {
        this.students[idx].groupId = row + 1;
        this.students[idx].groupLeader = col === 0 ? 1 : 0;
      }
    },
    async saveGroup() {
      this.saving = true;
      try {
        const data = this.students
          .filter(s => s && s.userId)
          .map(s => ({
            userId: s.userId,
            groupId: s.groupId,
            groupLeader: s.groupLeader
          }));
        const res = await axios.post('/api/user/updateGroup', data);
        if (res.data && res.data.code === '000000') {
          ElMessage.success('保存成功');
          await this.loadStudents();
        } else {
          throw new Error(res.data.msg || '保存失败');
        }
      } catch (e) {
        ElMessage.error('保存失败: ' + (e.response?.data?.msg || e.message));
      } finally {
        this.saving = false;
      }
    },
  }
};
</script>

<style scoped>
.groupmap-container {
  padding: 32px;
  background: #f4f8fb;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
}
.groupmap-main {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin: 40px 0 0 0;
  align-items: center;
}
.group-row {
  display: flex;
  gap: 8px;
  padding: 6px 0;
  border-radius: 8px;
}
.group-cell {
  width: 90px;
  height: 40px;
  background: #4fa3f7;
  color: #fff;
  border: none;
  border-radius: 6px;
  font-size: 1rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;
}
.group-cell:empty {
  background: #e0e0e0;
  cursor: default;
}
.group-cell[draggable="true"] {
  cursor: grab;
}
.group-cell[draggable="true"]:active {
  cursor: grabbing;
}
.save-btn {
  margin: 32px auto 0 auto;
  display: block;
  padding: 12px 48px;
  font-size: 1.1rem;
}
@media (max-width: 900px) {
  .groupmap-main {
    gap: 4px;
  }
  .group-cell {
    width: 40px;
    height: 22px;
    font-size: 0.7rem;
    padding: 0;
  }
  .save-btn {
    padding: 8px 20px;
    font-size: 0.9rem;
  }
}
</style> 